import React, { useEffect, useState } from "react";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
    commonservices,
    getCardBrandImage,
    getNormalisedBrandName,
} from "../services/CommonServices";
import {
    CreatePayment,
    GetWalletDataList,
    PaymentMethodList,
    PaymentMethod_CreatePayment,
    TopupWalletPayment,
} from "../services/ReduxTookit/Slice/InvoiceRedux";
import { UpdatePlan } from "../services/ReduxTookit/Slice/SubscriptionsRedux";
import SubmitLoading from "../component/common-component/Loaders/SubmitLoading";
import MainpageLoader from "../component/common-component/Loaders/MainpageLoader";
import Loader, { LoaderType } from "../component/common-component/Loader";

import infoIcon from "../assets/images/infobtnblue.svg";
import greenTick from "../assets/images/greenTick.svg";
import editPencil from "../assets/images/editpencilIcon.svg";

const formatAmountNoRound = (val, decimals = 2) => {
    if (val === null || val === undefined || val === "") return "0.00";
    const num = Number(val);
    if (isNaN(num)) return "0.00";
    if (num === 0) return "0.00";
    const fixedStr = num.toFixed(8);
    const [intPart, decPart = ""] = fixedStr.split(".");
    return `${intPart}.${decPart.slice(0, decimals)}`;
};

const parseAmountNoRound = (val, decimals = 2) => {
    return Number(formatAmountNoRound(val, decimals));
};

const PaymentMethodSelection = (props) => {
    const dispatch = useDispatch();
    const ProfileInfo = useSelector((state) => state?.userinfo?.UserInfo);

    const isSubscription =
        props?.itemtype?.toLowerCase()?.includes("subscription") ||
        props?.item?.itemtype?.toLowerCase()?.includes("subscription");
    const isWalletTopup = props?.itemtype?.toLowerCase() === "wallet";
    const isAutoReceipt = isSubscription || isWalletTopup;
    const amountLabel = isSubscription ? "Subscription Amount" : isWalletTopup ? "Top-Up Amount" : "Invoice Amount";
    const amountTypeText = isSubscription ? "subscription" : isWalletTopup ? "top-up" : "invoice";

    const [loading, setLoading] = useState(false);
    const [walletLoading, setWalletLoading] = useState(false);
    const [walletBalance, setWalletBalance] = useState(0);
    const [useWallet, setUseWallet] = useState(true);
    const [paymentMethodData, setPaymentMethodData] = useState([]);
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState("");

    const rawInvoiceFinalAmount = props?.item?.invoicefinalamount
        ? Number(props.item.invoicefinalamount)
        : props?.item?.invoicenetamount
            ? Number(props.item.invoicenetamount)
            : 0;
    const invoiceFinalAmount = parseAmountNoRound(rawInvoiceFinalAmount, 2);

    // Dynamic calculations
    const isWalletApplicable = !isWalletTopup && useWallet && walletBalance > 0;
    const walletDeduction = isWalletApplicable
        ? parseAmountNoRound(Math.min(walletBalance, invoiceFinalAmount), 2)
        : 0;
    const cardPayment = parseAmountNoRound(Math.max(0, invoiceFinalAmount - walletDeduction), 2);
    const isWalletSufficient = isWalletApplicable && walletBalance >= invoiceFinalAmount;
    const totalPayable = invoiceFinalAmount;

    // Fetch Wallet Details from API: Subscribers/WalletDetailBySubscriberId
    const fetchWalletDetails = async () => {
        setWalletLoading(true);
        try {
            const response = await dispatch(GetWalletDataList());
            setWalletLoading(false);
            if (response?.payload?.isValidate) {
                const rawBalance =
                    response?.payload?.data?.data?.dataList1?.[0]?.amount || 0;
                const balance = parseAmountNoRound(rawBalance, 2);
                setWalletBalance(balance);
                setUseWallet(balance > 0);
            } else {
                setWalletBalance(0);
                setUseWallet(false);
            }
        } catch (error) {
            setWalletLoading(false);
            setWalletBalance(0);
            setUseWallet(false);
        }
    };

    // Fetch Saved Payment Methods
    const getPaymentMethodList = async () => {
        setLoading(true);
        const body = {};
        try {
            const response = await dispatch(PaymentMethodList(body));
            setLoading(false);
            if (response?.payload?.isValidate) {
                const list = response?.payload?.data?.data || [];
                if (list && list.length > 0) {
                    const currentYear = new Date().getFullYear();
                    const currentMonth = new Date().getMonth() + 1;
                    const validCards = list.filter((d) => {
                        const expYear = parseInt(d.exp_year);
                        const expMonth = parseInt(d.exp_month);
                        return (
                            expYear > currentYear ||
                            (expYear === currentYear && expMonth >= currentMonth)
                        );
                    });

                    setPaymentMethodData(validCards);
                    if (validCards.length > 0) {
                        const primaryCard = validCards.find((p) => p.isprimary === true);
                        setSelectedPaymentMethodId(
                            primaryCard ? primaryCard.gatewaymethodid : validCards[0].gatewaymethodid
                        );
                    } else {
                        setSelectedPaymentMethodId("");
                    }
                } else {
                    setPaymentMethodData([]);
                    setSelectedPaymentMethodId("");
                }
            } else {
                setPaymentMethodData([]);
                setSelectedPaymentMethodId("");
            }
        } catch (error) {
            setLoading(false);
            setPaymentMethodData([]);
        }
    };

    useEffect(() => {
        if (props.show) {
            if (!isWalletTopup) {
                fetchWalletDetails();
            }
            getPaymentMethodList();
        }
    }, [props.show]);

    const getCardDisplayName = (item) => {
        const brand = getNormalisedBrandName(item?.display_brand || "");
        const category = item?.category
            ? item.category.charAt(0).toUpperCase() +
            item.category.slice(1).toLowerCase()
            : "";
        if (category) {
            return `${brand} ${category} Card`;
        }
        return brand ? `${brand} Card` : "Credit / Debit Card";
    };

    const formatCardMask = (methodNumber) => {
        const last4 = methodNumber ? methodNumber.slice(-4) : "••••";
        return `•••• •••• •••• ${last4}`;
    };

    const billingDateStr = props?.item?.dateofinvoice
        ? commonservices.getDateInFormatCustom(
            props.item.dateofinvoice,
            null,
            "MM/DD/YYYY"
        )
        : props?.item?.createdon
            ? commonservices.getDateInFormatCustom(
                props.item.createdon,
                null,
                "MM/DD/YYYY"
            )
            : commonservices.getDateInFormatCustom(
                new Date(),
                null,
                "MM/DD/YYYY"
            );

    const dueDateStr = isAutoReceipt
        ? "N/A"
        : props?.item?.invoiceduedate
            ? commonservices.getDateInFormatCustom(
                props.item.invoiceduedate,
                null,
                "MM/DD/YYYY"
            )
            : props?.item?.duedate
                ? commonservices.getDateInFormatCustom(
                    props.item.duedate,
                    null,
                    "MM/DD/YYYY"
                )
                : "-";

    const fnCreatePayment = async () => {
        if (isWalletTopup) {
            setLoading(true);
            try {
                const body = {
                    amount: invoiceFinalAmount,
                    returnurl:
                        window.location.origin + "/management-listing?from=wallet&session_id={CHECKOUT_SESSION_ID}",
                    associationid: props?.item?.walletid,
                    associationtype: "wallet",
                    gatewaymethodid: selectedPaymentMethodId || "",
                };
                const rest = await dispatch(TopupWalletPayment(body));
                setLoading(false);
                if (
                    rest &&
                    rest.payload &&
                    rest.payload.isValidate &&
                    rest.payload.data
                ) {
                    if (rest.payload.data.status_code === 1) {
                        if (rest.payload.data.data) {
                            // Redirect to Stripe Checkout for top-up payment
                            window.location = rest.payload.data.data;
                        } else {
                            toast.success(rest.payload.data.message || "Wallet topped up successfully.");
                            if (props?.onPaymentSuccess) {
                                props.onPaymentSuccess();
                            }
                            if (props?.onHide) {
                                props.onHide();
                            }
                        }
                    } else {
                        toast.error(rest.payload.data.message || "Something went wrong.");
                    }
                } else {
                    toast.error(rest?.payload?.data?.message || "Failed to initiate top-up payment.");
                }
            } catch (error) {
                setLoading(false);
                toast.error("An error occurred while processing top-up payment.");
            }
            return;
        }

        if (isSubscription) {
            setLoading(true);
            try {
                const currentPath = props?.returnpath || window.location.pathname || "/management-listing";
                const payload = {
                    oldnoofmonth: props.item?.oldnoofmonth || 1,
                    oldsubscriptionid: props.item?.oldsubscriptionid || 0,
                    newsubscriptionid: props.item?.subscriptionid || props.item?.newsubscriptionid || 0,
                    newnoofmonth: props.item?.newnoofmonth || 1,
                    returnurl:
                        window.location.origin + currentPath + "?from=subscription&session_id={CHECKOUT_SESSION_ID}",
                    ischangeinusertype: props.item?.ischangeinusertype || false,
                    ischangeinsubscription: true,
                    isworkflowchecked: props.item?.isworkflowchecked || false,
                    isdeveloperchecked: props.item?.isdeveloperchecked || false,
                    contactlist: props.item?.contactlist || [],
                    ischangeinworkflowmanagement: props.item?.ischangeinworkflowmanagement || false,
                    ischangeindeveloper: props.item?.ischangeindeveloper || false,
                    iswalletapplicable: isWalletApplicable,
                    walletamount: walletDeduction,
                    gatewaymethodid: selectedPaymentMethodId || "",
                };
                const rest = await dispatch(UpdatePlan(payload));
                setLoading(false);
                if (
                    rest &&
                    rest.payload &&
                    rest.payload.isValidate &&
                    rest.payload.data
                ) {
                    if (rest.payload.data.status_code === 1) {
                        if (rest.payload.data.data) {
                            // Redirect to Stripe Checkout for subscription payment
                            window.location = rest.payload.data.data;
                        } else {
                            // 100% Paid via Wallet
                            toast.success(rest.payload.data.message || "Subscription updated successfully.");
                            if (props?.onPaymentSuccess) {
                                props.onPaymentSuccess();
                            }
                            if (props?.onHide) {
                                props.onHide();
                            }
                        }
                    } else {
                        toast.error(rest.payload.data.message || "Something went wrong.");
                    }
                } else {
                    toast.error(rest?.payload?.data?.message || "Failed to initiate subscription payment.");
                }
            } catch (error) {
                setLoading(false);
                toast.error("An error occurred while processing subscription payment.");
            }
            return;
        }

        if (!props?.item?.invoiceid) {
            toast.error("Invoice details not found.");
            return;
        }
        setLoading(true);
        try {
            const currentPath = props?.returnpath || window.location.pathname || "/management-listing";
            const body = {
                amount: cardPayment,
                invoiceno: props?.item?.invoiceno || "",
                returnurl:
                    window.location.origin + currentPath + "?from=bill&session_id={CHECKOUT_SESSION_ID}",
                associationid: props?.item?.invoiceid,
                associationtype: props?.itemtype || "Workflow Invoice",
                gatewaymethodid: selectedPaymentMethodId || "",
                isWalletApplicable: isWalletApplicable,
                walletamount: walletDeduction,
            };
            const rest = await dispatch(CreatePayment(body));
            setLoading(false);
            if (
                rest &&
                rest.payload &&
                rest.payload.isValidate &&
                rest.payload.data
            ) {
                if (rest.payload.data.status_code === 1) {
                    if (rest.payload.data.data) {
                        // Redirect to Stripe Checkout for card payment
                        window.location = rest.payload.data.data;
                    } else {
                        // 100% Paid via Wallet (no Stripe session needed)
                        toast.success(rest.payload.data.message || "Invoice paid successfully.");
                        if (props?.onPaymentSuccess) {
                            props.onPaymentSuccess();
                        }
                        if (props?.onHide) {
                            props.onHide();
                        }
                    }
                } else {
                    toast.error(rest.payload.data.message || "Something went wrong.");
                }
            } else {
                toast.error(rest?.payload?.data?.message || "Failed to initiate payment.");
            }
        } catch (error) {
            setLoading(false);
            toast.error("An error occurred while processing payment.");
        }
    };

    const fnAddNewPaymentMethod = async () => {
        setLoading(true);
        try {
            const currentPath = props?.returnpath || window.location.pathname || "/management-listing";
            if (props?.item) {
                sessionStorage.setItem(
                    "reopen_bill_payment_invoice",
                    JSON.stringify({
                        item: props.item,
                        itemtype: props.itemtype || (isSubscription ? "Subscription" : "Workflow Invoice"),
                    })
                );
            }
            const body = {
                amount: 1,
                invoiceno: (ProfileInfo?.subscriberid || "") + "00001",
                returnurl:
                    window.location.origin + currentPath + "?from=bill_add_card&session_id={CHECKOUT_SESSION_ID}",
                associationid: ProfileInfo?.subscriberid || 0,
                associationtype: "Subscriber Add Payment Method",
            };
            const rest = await dispatch(PaymentMethod_CreatePayment(body));
            setLoading(false);
            if (
                rest &&
                rest.payload &&
                rest.payload.isValidate &&
                rest.payload.data
            ) {
                if (rest.payload.data.status_code === 1 && rest.payload.data.data) {
                    window.location = rest.payload.data.data;
                } else {
                    toast.error(rest.payload.data.message || "Failed to initiate add payment method.");
                }
            } else {
                toast.error(rest?.payload?.data?.message || "Failed to initiate add payment method.");
            }
        } catch (error) {
            setLoading(false);
            toast.error("An error occurred while adding payment method.");
        }
    };

    const isWalletZero = walletBalance <= 0;

    return (
        <Modal
            {...props}
            size="lg"
            aria-labelledby="contained-modal-title-vcenter"
            centered
            backdrop="static"
            className="select-payment-method-modal"
        >
            {(loading || walletLoading) && <MainpageLoader />}
            <Modal.Header closeButton>
                <Modal.Title id="contained-modal-title-vcenter">
                    Select Payment Method
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-3 p-md-4">
                {/* Top Info Bar */}
                <div className="payment-invoice-top-bar mb-3">
                    <Row className="g-0 align-items-center">
                        <Col sm={3} className="info-bar-cell">
                            <span className="info-bar-label">{isAutoReceipt ? "Receipt #" : "Invoice #"}</span>
                            <span className="info-bar-value">{isAutoReceipt ? (props?.item?.invoiceno || "Auto Generate") : (props?.item?.invoiceno || "-")}</span>
                        </Col>
                        <Col sm={3} className="info-bar-cell">
                            <span className="info-bar-label">Billing Date</span>
                            <span className="info-bar-value">{billingDateStr}</span>
                        </Col>
                        <Col sm={3} className="info-bar-cell">
                            <span className="info-bar-label">Due Date</span>
                            <span className="info-bar-value">{isAutoReceipt ? "N/A" : dueDateStr}</span>
                        </Col>
                        <Col sm={3} className="info-bar-cell last-cell">
                            <span className="info-bar-label">{amountLabel}</span>
                            <span className="info-bar-value total-blue">
                                $ {formatAmountNoRound(invoiceFinalAmount)}
                            </span>
                        </Col>
                    </Row>
                </div>

                {/* 2-Column Main Section */}
                <Row className="g-3 g-md-4">
                    {/* Left Column: Wallet, Alert, Cards, Add Button */}
                    <Col lg={7} className="d-flex flex-column justify-content-between">
                        <div>
                            {!isWalletTopup && (
                            <>
                            {/* Wallet Box */}
                            <div
                                className={`payment-wallet-card mb-2 ${isWalletZero ? "disabled-wallet" : useWallet ? "active-wallet" : ""}`}
                                onClick={() => {
                                    if (!isWalletZero) {
                                        setUseWallet(!useWallet);
                                    }
                                }}
                            >
                                <div className="payment-wallet-card-left d-flex align-items-center">
                                    <Form.Check
                                        type="checkbox"
                                        id="wallet-checkbox"
                                        className="payment-custom-checkbox me-3 flex-shrink-0"
                                        disabled={isWalletZero}
                                        checked={!isWalletZero && useWallet}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            if (!isWalletZero) {
                                                setUseWallet(e.target.checked);
                                            }
                                        }}
                                    />
                                    <div className="wallet-icon-wrapper me-3 flex-shrink-0">
                                        <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M3 8C3 7.20435 3.31607 6.44129 3.87868 5.87868C4.44129 5.31607 5.20435 5 6 5H18C18.7956 5 19.5587 5.31607 20.1213 5.87868C20.6839 6.44129 21 7.20435 21 8V16C21 16.7956 20.6839 17.5587 20.1213 18.1213C19.5587 18.6839 18.7956 19 18 19H6C5.20435 19 4.44129 18.6839 3.87868 18.1213C3.31607 17.5587 3 16.7956 3 16V8Z"
                                                stroke="#0284c7"
                                                strokeWidth="1.8"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <path
                                                d="M3 10H21"
                                                stroke="#0284c7"
                                                strokeWidth="1.8"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <circle cx="17" cy="14.5" r="1.5" fill="#0284c7" />
                                        </svg>
                                    </div>
                                    <div className="wallet-card-text">
                                        <h6 className="wallet-card-title mb-1">Use Wallet Balance</h6>
                                        <p className="wallet-card-desc mb-0">
                                            We will automatically deduct from your wallet balance first.
                                        </p>
                                    </div>
                                </div>
                                <div className="wallet-balance-box text-end ps-3 flex-shrink-0">
                                    <span className="wallet-bal-label d-block">Available Balance</span>
                                    <span className="wallet-bal-amount">
                                        $ {formatAmountNoRound(walletBalance)}
                                    </span>
                                </div>
                            </div>

                            {/* Wallet Deduction Info Banner */}
                            <div className="wallet-info-alert mb-3">
                                <div className="d-flex align-items-start">
                                    <div className="info-icon-circle me-2 mt-1">
                                        <img src={infoIcon} alt="info" />
                                    </div>
                                    <div>
                                        {useWallet && isWalletSufficient ? (
                                            <>
                                                <p className="alert-text-bold mb-1">
                                                    $ {formatAmountNoRound(invoiceFinalAmount)} will be deducted from your wallet.
                                                </p>
                                                <p className="alert-text-muted mb-0">
                                                    Since your wallet balance is sufficient, no card will be charged.
                                                </p>
                                            </>
                                        ) : useWallet && walletBalance > 0 ? (
                                            <>
                                                <p className="alert-text-bold mb-1">
                                                    $ {formatAmountNoRound(walletDeduction)} will be deducted from your wallet.
                                                </p>
                                                <p className="alert-text-muted mb-0">
                                                    Remaining $ {formatAmountNoRound(cardPayment)} will be charged to your selected payment method.
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <p className="alert-text-bold mb-1">
                                                    {isWalletZero ? "Your wallet balance is $ 0.00." : "Wallet balance is not selected."}
                                                </p>
                                                <p className="alert-text-muted mb-0">
                                                    The full {amountTypeText} amount of $ {formatAmountNoRound(invoiceFinalAmount)} will be charged to your selected payment method.
                                                </p>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            </>
                            )}

                            {/* Select Payment Method Section */}
                            <div className="payment-cards-section">
                                <h6 className="cards-section-title mb-1">
                                    Select a Payment Method {useWallet && isWalletSufficient ? "(optional backup)" : isWalletApplicable ? "(for remaining amount)" : ""}
                                </h6>
                                <p className="cards-section-desc mb-2">
                                    This payment method will be used if wallet balance is not sufficient.
                                </p>

                                {/* Saved Cards List */}
                                <div className="saved-cards-list">
                                    {paymentMethodData && paymentMethodData.length > 0 ? (
                                        paymentMethodData.map((item) => {
                                            const isSelected =
                                                selectedPaymentMethodId === item?.gatewaymethodid;
                                            return (
                                                <div
                                                    key={item?.gatewaymethodid}
                                                    className={`saved-card-item ${isSelected ? "card-selected" : ""}`}
                                                    onClick={() =>
                                                        setSelectedPaymentMethodId(item?.gatewaymethodid)
                                                    }
                                                >
                                                    <div className="d-flex align-items-center flex-grow-1 min-w-0">
                                                        {/* Custom Circular Check Indicator */}
                                                        <div className={`custom-radio-indicator me-3 ${isSelected ? "selected" : ""}`}>
                                                            {isSelected && (
                                                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                                    <path
                                                                        d="M10 3L4.5 8.5L2 6"
                                                                        stroke="white"
                                                                        strokeWidth="2"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                    />
                                                                </svg>
                                                            )}
                                                        </div>

                                                        {/* Card Brand Image */}
                                                        <div className="card-brand-icon-box me-3">
                                                            {item?.display_brand &&
                                                                getCardBrandImage(item?.display_brand)}
                                                        </div>

                                                        {/* Card Info */}
                                                        <div className="card-details-text">
                                                            <div className="d-flex align-items-center gap-2 mb-1">
                                                                <span className="card-name-title">
                                                                    {getCardDisplayName(item)}
                                                                </span>
                                                                {item?.isprimary && (
                                                                    <span className="card-default-badge">
                                                                        Default
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <span className="card-mask-number">
                                                                {formatCardMask(item?.methodnumber)}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Edit Pencil Icon */}
                                                    <div
                                                        className="card-edit-btn"
                                                        title="Manage Payment Methods"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            window.open("/management-listing?from=paymentmethod", "_blank");
                                                        }}
                                                    >
                                                        <img src={editPencil} alt="edit" />
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="no-cards-placeholder p-3 text-center border rounded">
                                            <p className="text-muted mb-0">No saved payment methods found.</p>
                                        </div>
                                    )}
                                </div>

                                {/* Add New Payment Method Dashed Button */}
                                <button
                                    type="button"
                                    className="add-payment-dashed-btn mt-2"
                                    onClick={fnAddNewPaymentMethod}
                                    disabled={loading}
                                >
                                    <span className="plus-icon me-1">+</span> Add New Payment Method
                                </button>
                            </div>
                        </div>
                    </Col>

                    {/* Right Column: Payment Summary, Total Payable, Status Banner & Important Notes */}
                    <Col lg={5} className="d-flex flex-column justify-content-between">
                        {/* Payment Summary Box */}
                        <div className="payment-summary-box mb-3">
                            <div className="summary-header d-flex align-items-center mb-3">
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    className="me-2"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M9 12H15M9 16H13M19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H14L21 10V19C21 20.1046 20.1046 21 19 21Z"
                                        stroke="#1e293b"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <h6 className="summary-title mb-0">Payment Summary</h6>
                            </div>

                            <div className="summary-row d-flex justify-content-between align-items-center mb-2">
                                <span className="summary-row-label">{amountLabel}</span>
                                <span className="summary-row-value">
                                    $ {formatAmountNoRound(invoiceFinalAmount)}
                                </span>
                            </div>
                            <div className="summary-row d-flex justify-content-between align-items-center mb-2">
                                <span className="summary-row-label">Wallet Payment</span>
                                <span className="summary-row-value text-success fw-semibold">
                                    $ {formatAmountNoRound(walletDeduction)}
                                </span>
                            </div>
                            <div className="summary-row d-flex justify-content-between align-items-center mb-3">
                                <span className="summary-row-label">Card Payment</span>
                                <span className="summary-row-value">
                                    $ {formatAmountNoRound(cardPayment)}
                                </span>
                            </div>

                            {/* Total Payable Box */}
                            <div className="payment-total-payable-card d-flex justify-content-between align-items-center mb-3">
                                <span className="total-payable-label">Total Payable</span>
                                <span className="total-payable-amount">
                                    $ {formatAmountNoRound(totalPayable)}
                                </span>
                            </div>

                            {/* Status Alert Banner */}
                            {useWallet && isWalletSufficient ? (
                                <div className="summary-status-banner green-banner d-flex align-items-center">
                                    <div className="status-tick-icon me-2">
                                        <img src={greenTick} alt="success" />
                                    </div>
                                    <span className="status-text">
                                        Your wallet balance can cover the full {amountTypeText} amount.
                                    </span>
                                </div>
                            ) : useWallet && walletBalance > 0 ? (
                                <div className="summary-status-banner blue-banner d-flex align-items-center">
                                    <div className="status-tick-icon me-2">
                                        <img src={infoIcon} alt="info" />
                                    </div>
                                    <span className="status-text">
                                        Wallet covers $ {formatAmountNoRound(walletDeduction)}. Remaining $ {formatAmountNoRound(cardPayment)} via card.
                                    </span>
                                </div>
                            ) : (
                                <div className="summary-status-banner grey-banner d-flex align-items-center">
                                    <div className="status-tick-icon me-2">
                                        <img src={infoIcon} alt="info" />
                                    </div>
                                    <span className="status-text">
                                        Full {amountTypeText} amount of $ {formatAmountNoRound(invoiceFinalAmount)} will be charged to card.
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Important Notes Box */}
                        <div className="important-notes-box">
                            <div className="notes-header d-flex align-items-center mb-2">
                                <div className="notes-info-icon me-2">
                                    <img src={infoIcon} alt="info" />
                                </div>
                                <h6 className="notes-title mb-0">Important Notes</h6>
                            </div>
                            <ul className="notes-list mb-0 ps-3">
                                <li>If your wallet balance is selected, it will be used first.</li>
                                <li>
                                    The remaining amount (if any) will be charged to your selected
                                    payment method.
                                </li>
                                <li>
                                    You can manage your payment methods from your account settings.
                                </li>
                            </ul>
                        </div>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer className="payment-modal-footer">
                <button
                    type="button"
                    className="grey-btn w-auto"
                    onClick={props.onHide}
                    disabled={loading}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    disabled={loading}
                    className="blue-btn w-auto pay-action-btn"
                    onClick={fnCreatePayment}
                >
                    {loading ? (
                        <SubmitLoading />
                    ) : (
                        `Pay $ ${formatAmountNoRound(totalPayable)}`
                    )}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default PaymentMethodSelection;
